import { QueryClient } from '@tanstack/react-query';

// Timeout padrão para queries (20 segundos)
// Evita queries penduradas indefinidamente após suspensão de aba
const QUERY_TIMEOUT_MS = 20 * 1000;

// Wrapper para adicionar timeout a qualquer queryFn
export function withTimeout<T>(
  queryFn: (context: { signal?: AbortSignal }) => Promise<T>,
  timeoutMs: number = QUERY_TIMEOUT_MS
): (context: { signal?: AbortSignal }) => Promise<T> {
  return async (context) => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => {
      controller.abort(new Error(`Query timeout após ${timeoutMs / 1000}s`));
    }, timeoutMs);

    // Combinar signal externo com timeout
    if (context.signal) {
      context.signal.addEventListener('abort', () => {
        clearTimeout(timeoutId);
        controller.abort(context.signal?.reason);
      });
    }

    try {
      return await queryFn({ signal: controller.signal });
    } finally {
      clearTimeout(timeoutId);
    }
  };
}

// Configuração otimizada para performance com grandes volumes de dados
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Cache por 15 minutos (balance entre egress e freshness)
      // ⚠️ EGRESS FIX: aumentado de 5 min → 15 min para reduzir refetches
      // Com 20 usuários, 5 min causava ~288 fetches/dia/usuário = egress excessivo
      staleTime: 15 * 60 * 1000,
      // Mantém cache por 1 hora
      gcTime: 60 * 60 * 1000,
      // Retry inteligente: não fazer retry em cancelamentos/timeouts
      retry: (failureCount, error) => {
        const isCancelOrTimeout = (msg: string) =>
          msg.includes('cancel') || msg.includes('abort') || msg.includes('timeout');

        // Erros JS nativos (Error instances)
        if (error instanceof Error && isCancelOrTimeout(error.message.toLowerCase())) {
          return false;
        }

        // Objetos de erro do Supabase/PostgREST (não são instanceof Error)
        if (error && typeof error === 'object' && 'message' in error) {
          const sbError = error as { message?: string; code?: string };
          // 57014 = statement_timeout (PostgreSQL cancela a query por demora)
          if (sbError.code === '57014') return false;
          if (sbError.message && isCancelOrTimeout(sbError.message.toLowerCase())) {
            return false;
          }
        }

        // Máximo 1 retry para outros erros
        return failureCount < 1;
      },
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      // Desabilitar refetch automático no foco - useVisibilityRefresh cuida disso
      refetchOnWindowFocus: false,
      // ⚠️ EGRESS FIX: desabilitar refetch ao reconectar
      // useVisibilityRefresh já cuida de refresh após inatividade
      refetchOnReconnect: false,
      // ⚠️ EGRESS FIX: false = NÃO refetch automaticamente ao montar componente
      // Dados em cache são usados até expirar (staleTime 15 min)
      // DateRangeProvider invalida cache ao mudar datas, forçando refetch quando necessário
      // Com true: cada navegação de página causava 10+ refetches desnecessários
      refetchOnMount: false,
      // Desabilitar refetch em intervalos para reduzir carga
      refetchInterval: false,
      // Configuração específica para grandes datasets
      structuralSharing: true, // Otimiza re-renders comparando estruturas
      networkMode: 'always', // Não pausar queries por status de rede — evita loading infinito ao navegar entre páginas
    },
    mutations: {
      // Retry 2 vezes para mutations
      retry: 2,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      // Cache mutations por 5 minutos
      gcTime: 5 * 60 * 1000,
    },
  },
});

// Configuração específica para queries de marketing/financial data
// ⚠️ EGRESS FIX: aumentado staleTime de 15 → 30 min, gcTime de 1h → 2h
export const financialQueryOptions = {
  staleTime: 30 * 60 * 1000, // 30 minutos para dados financeiros
  gcTime: 2 * 60 * 60 * 1000, // 2 horas de cache
  retry: 2,
  retryDelay: (attemptIndex: number) => Math.min(2000 * 2 ** attemptIndex, 60000),
  refetchOnMount: false,
};

// Configuração para queries de views materializadas (já otimizadas no banco)
// ⚠️ EGRESS FIX: aumentado staleTime de 30 → 60 min, gcTime de 2h → 4h
export const materializedViewQueryOptions = {
  staleTime: 60 * 60 * 1000, // 1 hora para views materializadas
  gcTime: 4 * 60 * 60 * 1000, // 4 horas de cache
  retry: 1,
  refetchOnMount: false, // Não refetch se já tem dados
};